import React from 'react';
import moment from 'moment/moment';
import { SelectOption } from 'global';
import { grey } from '@mui/material/colors';
import { SelectField } from 'components/form';
import { DateFormats } from 'constants/common';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CreditSubmission } from '@trnsact/trnsact-shared-types';
import { decisionCodeToStatus } from 'modules/creditSubmission/constants';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { LenderDeskingPanelFormValues } from '../../../types';

const decisionOptions: SelectOption[] = [
  { value: '803370002', label: 'Declined' },
  { value: '803370003', label: 'Approved' },
  { value: '803370004', label: 'Pre-Approved' },
  { value: '803370000', label: 'Awaiting Review' },
  { value: '803370001', label: 'Requested Additional Information' },
];

const DECLINE_DECISIONS = ['803370002', '803370001'];

interface Props {
  isCSDecisionPosting: boolean;
  cs: CreditSubmission & Record<string, any>;
  handleDecisionSave: (value: string) => void;
}

export const DecisionSection = ({ cs, isCSDecisionPosting, handleDecisionSave }: Props) => {
  const {
    control,
    formState: { dirtyFields },
  } = useFormContext<LenderDeskingPanelFormValues>();

  const decision = useWatch({ control, name: 'submission.decision' });

  const csDecision = cs.decision
    ? decisionCodeToStatus[cs.decision as keyof typeof decisionCodeToStatus]?.toString()
    : null;

  const isDeclineDataChanged =
    DECLINE_DECISIONS.includes(decision ?? '') &&
    (dirtyFields.submission?.notes || dirtyFields.creditSubmissionDeclineReason);

  const isSaveDisabled = isCSDecisionPosting || (decision === csDecision && !isDeclineDataChanged);

  const [financeDecisionInformation] = cs?.decisions ?? [];
  const showApproveInfo = financeDecisionInformation && cs?.decision === 'Approved' && decision === '803370003';

  return (
    <Stack
      gap="1rem"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ padding: '1rem 1.5rem', border: `1px solid ${grey[300]}`, borderRadius: '0.25rem' }}
    >
      <Stack gap="1rem" direction="row" alignItems="center">
        <Typography variant="h6" component="span">
          Decision
        </Typography>

        <Box width="20rem">
          <SelectField
            control={control}
            variant="outlined"
            label="Decision Status"
            options={decisionOptions}
            name="submission.decision"
          />
        </Box>

        <RenderWithCondition condition={decision !== '803370003'}>
          <Button variant="contained" onClick={() => handleDecisionSave(decision!)} disabled={isSaveDisabled}>
            Save
          </Button>
        </RenderWithCondition>
      </Stack>

      {showApproveInfo && (
        <Stack>
          <Typography variant="body2" color="text.secondary">
            Decided by {financeDecisionInformation.createdBy.fullName} at{' '}
            {moment(financeDecisionInformation.createdDateTime).format(DateFormats.DateTimeMedium24Hour)}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
